import { defineStore } from "pinia";

import axios from "../../axios-auth";
import { i18n } from "../../i18n.js";
import qs from "qs";
import { pathOr } from "ramda";

import { useWebproductStore } from "@/stores/modules/webproduct";
import { useUrlsStore } from "@/stores/modules/urls";
import { useMyAccountStore } from "@/stores/modules/myAccount";
import { useBundleStore } from "@/stores/modules/bundle";
import { usePersonalizeStore } from "@/stores/modules/personalize";
import { usePreflightPitstopStore } from "@/stores/modules/preflightPitstop";
import { usePreview3DStore } from "@/stores/modules/preview3D";
import { usePreflightDataStore } from "@/stores/modules/preflightData";
import { callbackService } from "../../helpers/callbackService";
import { useQuoteStore } from "@/stores/modules/quote";

export const useUploadsStore = defineStore("uploads", {
  state: () => ({
    // hold the state of the Uploads, per product part
    uploads: {},
    ppEmptyState: {
      statusUploaded: false,
      previewUploaded: false,
      previewFile: "",
      previewFileSecond: "",
      previewFileLink: "",
      fileUrl: "",
      isLoading: false,
      loadingProgress: 0,
      showPreflight: false,
      uploadedFile: null,
      uploadedFileHash: null,
      uploadedFileRelativePath: null,
      pitstopReport: false,
      showRearrange: false,
      loading: {
        isLoading: true,
        fullPage: false,
        height: 100,
        width: 100
      }
    }
  }),

  getters: {
    getUploads(state) {
      return state.uploads;
    },
    getUploadForProductPart(state) {
      return function (pp) {
        return state.uploads[pp];
      };
    },
    getUploadRelativePathForProductPart(state) {
      return function (pp) {
        return pathOr(
          false,
          ["uploads", pp, "uploadedFileRelativePath"],
          state
        );
      };
    },
    getUploadItemId(state) {
      return function (pp) {
        const webproductStore = useWebproductStore();
        if (webproductStore.getIsMobile) {
          return webproductStore.getSelector + "_" + "mobile_dropzone_" + pp;
        }
        return webproductStore.getSelector + "_" + "dropzone_" + pp;
      };
    },
    getUploadItemIdBoth(state) {
      return function (pp) {
        const webproductStore = useWebproductStore();
        return [
          webproductStore.getSelector + "_" + "dropzone_" + pp,
          webproductStore.getSelector + "_" + "mobile_dropzone_" + pp
        ];
      };
    },
    getFileParameters(state) {
      return function (fileName) {
        return (
          "path=" +
          fileName.match(/.*\//) +
          "&filename=" +
          fileName.replace(/.*\//, "")
        );
      };
    },
    getUploadsForPP(state) {
      const data = [];
      for (var pp in state.uploads) {
        if (state.uploads[pp].statusUploaded) {
          data.push(pp);
        }
      }
      return data;
    }
  },

  actions: {
    setAttributeUploadForProductPart(payload) {
      if (this.uploads[payload.pp]) {
        var uploads = { ...this.uploads };
        uploads[payload.pp] = {
          ...uploads[payload.pp],
          [payload.key]: payload.value
        };

        this.uploads = uploads;
      }
    },
    setInitialUploads(payload) {
      var uploads = {};
      for (var code in payload) {
        uploads[payload[code]] = { ...this.ppEmptyState };
      }

      this.uploads = uploads;
    },
    setAttributeMultiUploadForProductPart(payload) {
      if (this.uploads[payload.pp]) {
        var uploads = { ...this.uploads };
        uploads[payload.pp] = {
          ...uploads[payload.pp],
          ...payload.obj
        };

        this.uploads = uploads;
      }
    },
    uploadOnPreview(payload) {
      const webproductStore = useWebproductStore();

      if (this.getUploadForProductPart(payload)["showRearrange"]) {
        webproductStore.openFancybox({
          src: this.getUploadForProductPart(payload)["previewFileLink"]
        });
      } else {
        var id = this.getUploadItemId(payload);
        if (id) {
          var elem = document.getElementById(id + "Img");
          if (elem) {
            elem.click();
          }
        }
      }
    },
    uploadOnDownload(payload) {
      const fileUrl = this.getUploadForProductPart(payload)["fileUrl"];
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    },
    uploadOnViewReport(payload) {
      const webproductStore = useWebproductStore();

      const urlsStore = useUrlsStore();
      var url =
        urlsStore.getUrl(axios.checkPreflight) +
        "?query=pdf_report&file=" +
        this.getUploadForProductPart(payload)["uploadedFileHash"];

      webproductStore.openFancybox({
        src: url
      });
    },
    uploadOnRearrange(payload) {
      const urlsStore = useUrlsStore();
      const webproductStore = useWebproductStore();

      const uploadedFileRelativePath =
        this.getUploadForProductPart(payload)["uploadedFileRelativePath"];
      if (uploadedFileRelativePath) {
        var url =
          urlsStore.getUrl(axios.rearrangePdf) +
          "?" +
          this.getFileParameters(uploadedFileRelativePath) +
          "&lang=" +
          i18n.locale;

        webproductStore.openFancybox({
          src: url
        });
      }
    },
    uploadOnDelete(payload) {
      const webproductStore = useWebproductStore();
      const myAccountStore = useMyAccountStore();
      const urlsStore = useUrlsStore();
      const bundleStore = useBundleStore();
      const personalizeStore = usePersonalizeStore();
      const preflightPitstopStore = usePreflightPitstopStore();
      const preview3DStore = usePreview3DStore();
      const preflightDataStore = usePreflightDataStore();
      const quoteStore = useQuoteStore();
      const that = this;
      function deleteItem() {
        const uploadedFile =
          that.getUploadForProductPart(payload)["uploadedFile"];
        const uploadedFileHash =
          that.getUploadForProductPart(payload)["uploadedFileHash"];
        if (!uploadedFile) {
          return;
        }
        that.setAttributeMultiUploadForProductPart({
          pp: payload,
          obj: {
            statusUploaded: false,
            previewUploaded: false,
            previewFile: "",
            previewFileSecond: "",
            previewFileLink: ""
          }
        });
        const uploadIdsBoth = that.getUploadItemIdBoth(payload);
        for (var itemId in uploadIdsBoth) {
          var item = document.getElementById(uploadIdsBoth[itemId]);
          if (item) {
            item.dropzone.removeAllFiles();
          }
        }

        if (webproductStore.getIsPreflighPitstop && uploadedFileHash) {
          preflightPitstopStore.pitstopDeleteFile(uploadedFile);
        }
        if (myAccountStore.getIsMyAccount) {
          callbackService.sendCallback(
            callbackService.callbacks.toggleReadyToConnect,
            false
          );
        }
        if (webproductStore.getIsPackingEnabled) {
          // reset the uploaded images for 3D
          preview3DStore.setPreview3DImage([]);
        }

        preflightDataStore.removePreflightDataPP(payload);
        preflightPitstopStore.removePreflightInfoPP(payload);
        webproductStore.removeUploadedFile(payload);
        webproductStore.setFilesNeedToBeConnected(true);
        quoteStore.removeDefaultSelectionUploads(payload);

        var deleteParams = {
          selection: webproductStore.getSelectionId,
          filename: uploadedFile,
          store_code: webproductStore.getStoreCode
        };

        axios.instance
          .post(urlsStore.getUrl(axios.deleteFile), qs.stringify(deleteParams))
          .then(response => {
            if (!response.data.success) {
              console.log(response.data.message);
            }
            that.setAttributeMultiUploadForProductPart({
              pp: payload,
              obj: { uploadedFile: null, uploadedFileHash: null }
            });
          })
          .catch(error => {
            console.log(error);
          });
      }

      if (
        bundleStore.getBundleIsActive &&
        bundleStore.getBundleIsPdfVt &&
        payload == bundleStore.getBundleMainProductPart &&
        personalizeStore.getPPWithCustomizedProductId.length > 0
      ) {
        webproductStore
          .getSweetAlert({
            text: i18n.t(
              "If you delete these data, you will need to re-create the personalizations !"
            ),
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
            cancelButtonText: i18n.t("Cancel"),
            confirmButtonText: i18n.t("Ok")
          })
          .then(userAction => {
            if (userAction.isConfirmed) {
              personalizeStore.setBundleEmptyCustomizedProductId();
              deleteItem();
            } else {
              return;
            }
          });
      } else {
        deleteItem();
      }
    }
  }
});
